import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { PaginatorModule } from 'primeng/paginator';
import { SkeletonModule } from 'primeng/skeleton';

import { BehaviorSubject, Observable, combineLatest, map, shareReplay } from 'rxjs';
import { CorporateReportingStoreService } from '../../../services/stores/corporate-reporting-store.service';
import { HighRiskReportRow } from '../../../domain/corporate/high-risk-report-row';
import { DEFAULT_REPORT_DAYS_RISK_THRESHOLDS } from '../../../domain/corporate/report-days-risk-thresholds';
import { RiskLevel } from '../../../domain/corporate/risk-level';
import { ReportPropertyBreakdownRow } from '../../../domain/corporate/report-property-breakdown-row';
import { ButtonDirective } from 'primeng/button';

type SortOrder = 'desc' | 'asc';

@Component({
   selector: 'app-high-risk-reports-list',
   standalone: true,
   imports: [
      CommonModule,
      FormsModule,
      DropdownModule,
      PaginatorModule,
      SkeletonModule,
      ButtonDirective
   ],
   templateUrl: './high-risk-reports-list.component.html',
   styleUrls: ['./high-risk-reports-list.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class HighRiskReportsListComponent {
   readonly thresholds = DEFAULT_REPORT_DAYS_RISK_THRESHOLDS;

   // declare only (no initializer referencing this.store)
   readonly loading$!: Observable<boolean>;
   readonly errorStatus$!: Observable<number | null>;
   readonly vm$!: Observable<{
      rows: HighRiskReportRow[];
      totalRecords: number;
      first: number;
   }>;

   private sortSubject = new BehaviorSubject<SortOrder>('desc'); // Max -> Min default
   readonly sort$ = this.sortSubject.asObservable();

   private firstSubject = new BehaviorSubject<number>(0);
   readonly first$ = this.firstSubject.asObservable();
   readonly breakdownByName$!: Observable<
      Record<string, ReportPropertyBreakdownRow[]>
   >;

   private expanded = new Set<string>();

   rowsPerPage = 5;

   sortOptions = [
      { label: 'Av. Days (High → Low)', value: 'desc' as SortOrder },
      { label: 'Av. Days (Low → High)', value: 'asc' as SortOrder }
   ];
   sortOrder: SortOrder = 'desc';

   constructor(private store: CorporateReportingStoreService) {
      this.breakdownByName$ = this.store.reportBreakdownByName$;
      this.loading$ = this.store.reportsLoading$;
      this.errorStatus$ = this.store.reportsErrorStatus$;

      this.vm$ = combineLatest([
         this.store.highRiskReportRows$,
         this.sort$,
         this.first$
      ]).pipe(
         map(([rows, sort, first]) => {
            const sorted = [...rows].sort((a, b) => {
               const diff = a.avgDaysToComplete - b.avgDaysToComplete;
               return sort === 'asc' ? diff : -diff;
            });

            const pageRows = sorted.slice(first, first + this.rowsPerPage);

            return {
               rows: pageRows,
               totalRecords: sorted.length,
               first
            };
         }),
         shareReplay({ bufferSize: 1, refCount: true })
      );
   }

   onSortChange(): void {
      this.firstSubject.next(0);
      this.sortSubject.next(this.sortOrder);
   }

   onPageChange(event: any): void {
      this.firstSubject.next(event?.first ?? 0);
   }

   riskClass(level: RiskLevel | null): string {
      if (!level) return 'risk-na';
      return level === 'High'
         ? 'risk-high'
         : level === 'Medium'
           ? 'risk-medium'
           : 'risk-low';
   }

   valueClass(level: RiskLevel): string {
      return level === 'High'
         ? 'value-high'
         : level === 'Medium'
           ? 'value-medium'
           : 'value-low';
   }

   trackByName(_: number, row: HighRiskReportRow): string {
      return row.name;
   }

   isExpanded(reportName: string): boolean {
      return this.expanded.has(reportName);
   }

   toggleExpand(reportName: string): void {
      if (this.expanded.has(reportName)) this.expanded.delete(reportName);
      else this.expanded.add(reportName);
   }
}
